import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, SwatchBook, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DashboardDateRangePicker } from '@/components/ui/dashboard-date-range-picker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableSkeleton } from '@/components/skeletons';
import { GOAL_LABELS, nicheLabel, STATS_PERIOD_OPTIONS, STATUS_LABELS, STATUS_TONES } from '@/lib/template-labels';
import { formatVisitorRate } from '@/lib/template-stats';
import { toastIfSilent } from '@/lib/template-toast';
import { formTemplatesService, type AdminTemplateList, type StatsWindow } from '@/services/formTemplates';
import { EmptyBlock } from './components/EmptyBlock';
import { GatedButton } from './components/GatedButton';
import { ImportDefinitionDialog } from './components/ImportDefinitionDialog';
import { InfoTip } from './components/InfoTip';
import { NewTemplateDialog } from './components/NewTemplateDialog';
import { SalesSummaryCard } from './components/SalesSummaryCard';
import { StatText } from './components/StatText';
import { SummaryCardSkeleton } from './components/TemplateSkeletons';

const COLUMN_COUNT = 8;

const TemplatesList = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState<StatsWindow>(90);
  const [data, setData] = useState<AdminTemplateList | null>(null);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  // The period of the numbers on screen; a failed refetch puts the picker back to it.
  const loadedDays = useRef<StatsWindow | null>(null);
  // Only the newest request may write, so a slow answer can't replace a newer one.
  const latestRequest = useRef(0);

  const load = useCallback(async (requested: StatsWindow) => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const list = await formTemplatesService.list(requested);
      if (request !== latestRequest.current) return;
      loadedDays.current = list.days;
      setData(list);
      setDays(list.days);
    } catch (err) {
      if (request !== latestRequest.current) return;
      // The api client toasted a server message; anything else gets the fallback.
      toastIfSilent(err, "Couldn't load templates");
      // With nothing loaded yet the table shows the failure; otherwise the numbers stay and the picker goes back.
      if (loadedDays.current !== null) setDays(loadedDays.current);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(90);
    return () => {
      latestRequest.current++;
    };
  }, [load]);

  const changeDays = (next: StatsWindow) => {
    // A click on the pill that is already selected asks for nothing new.
    if (next === days) return;
    setDays(next);
    void load(next);
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="sm:min-w-48">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
            Templates{' '}
            {data !== null && <span className="text-muted-foreground font-normal">({data.templates.length})</span>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ready-made forms customers start from, and whether they sell.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <DashboardDateRangePicker
            value={String(days)}
            onChange={(value) => changeDays(Number(value) as StatsWindow)}
            options={STATS_PERIOD_OPTIONS}
            tooltipMessage="Filters imports, opt-in, bookings and trial to paid to the selected period."
          />
          <GatedButton
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="w-full sm:w-auto"
            wrapperClassName="w-full sm:w-auto"
          >
            <Upload />
            Import definition
          </GatedButton>
          <GatedButton
            onClick={() => setNewOpen(true)}
            className="w-full sm:w-auto"
            wrapperClassName="w-full sm:w-auto"
          >
            <Plus />
            New template
          </GatedButton>
        </div>
      </div>

      {loading ? (
        <SummaryCardSkeleton />
      ) : (
        data !== null && <SalesSummaryCard summary={data.summary} days={data.days} scope="library" />
      )}

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-full">Template</TableHead>
              <TableHead className="text-center">Niche</TableHead>
              <TableHead className="text-center">Goal</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Version</TableHead>
              <TableHead className="text-center">Imports</TableHead>
              <TableHead className="text-center whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  Opt-in rate
                  <InfoTip>Opt-in counts completed leads only.</InfoTip>
                </span>
              </TableHead>
              <TableHead className="text-center whitespace-nowrap">Bookings / visitor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeleton columns={COLUMN_COUNT} rows={5} />
            ) : data === null ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center">
                  <div className="flex flex-col items-center justify-center gap-3 py-8 text-sm text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Couldn't load templates
                    </span>
                    <Button variant="outline" size="sm" onClick={() => void load(days)}>
                      Try again
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : data.templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center">
                  <EmptyBlock
                    icon={SwatchBook}
                    title="No templates yet"
                    description="Create one, or import a definition written outside the product."
                  />
                </TableCell>
              </TableRow>
            ) : (
              data.templates.map((t) => (
                <TableRow
                  key={t.publicId}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/templates/${t.publicId}`)}
                >
                  <TableCell className="font-medium w-full">{t.name}</TableCell>
                  <TableCell className="text-center whitespace-nowrap">{nicheLabel(t.niche)}</TableCell>
                  <TableCell className="text-center whitespace-nowrap">{GOAL_LABELS[t.goal] ?? t.goal}</TableCell>
                  <TableCell className="text-center">
                    <Badge tone={STATUS_TONES[t.status]}>{STATUS_LABELS[t.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-center tabular-nums text-muted-foreground">
                    {t.currentVersionNo === null ? '—' : `v${t.currentVersionNo}`}
                  </TableCell>
                  <TableCell className="text-center tabular-nums">{t.stats.imports.toLocaleString('en-US')}</TableCell>
                  <TableCell className="text-center tabular-nums">
                    <StatText value={formatVisitorRate(t.stats.completedOptInRate, t.stats.uniqueVisitors)} />
                  </TableCell>
                  <TableCell className="text-center tabular-nums">
                    <StatText value={formatVisitorRate(t.stats.bookingsPerVisitor, t.stats.uniqueVisitors)} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ImportDefinitionDialog open={importOpen} onOpenChange={setImportOpen} />
      <NewTemplateDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
};

export default TemplatesList;
